import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ChevronDown, ChevronRight, FileText, Building, Home, ShieldCheck, Folder, Clock, Sparkles,
  Shield, Users, Briefcase, Award, Heart, Star, Layers, Check, PenLine, CalendarCheck,
} from 'lucide-react';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Mapping icône (string DB → composant lucide). Aligné avec AdminSubscriptionTypes.
const ICON_MAP = {
  'shield-check': ShieldCheck,
  shield: Shield,
  home: Home,
  users: Users,
  folder: Folder,
  briefcase: Briefcase,
  award: Award,
  heart: Heart,
  star: Star,
  layers: Layers,
  building: Building,
};

// Patterns historiques pour les types par défaut (priorité haute).
// Pour les types personnalisés, on tombe sur un matching par code/label.
const DEFAULT_PATTERNS = {
  rcds_batico: /\b(batico|rcds[_\s]?batico|attestation[_\s]?rcds|construction[_\s]?solidaire)\b/i,
  rcds_nominative: /\b(rcds[_\s]?nominat\w*|nominative)\b/i,
  dos_domium: /\b(domium|chantier|maitre[_\s]?ouvrage|m\.o\.|permis[_\s]?construire|daact|pv[_\s]?reception|dommages[_\s]?ouvrage)\b/i,
  ftpe_adhesion: /\b(ftpe|adhesion[_\s]?ftpe|pass[_\s]?ftpe|cotisation[_\s]?ftpe)\b/i,
};

// Construit un haystack lowercase une fois par doc.
function buildHaystack(doc) {
  const t = (doc?.type || doc?.type_document || '').toLowerCase();
  const cat = (doc?.categorie || '').toLowerCase();
  const nom = (doc?.nom || doc?.titre || doc?.label || '').toLowerCase();
  const ref = (doc?.reference || '').toLowerCase();
  return { full: `${t} ${cat} ${nom} ${ref}`, type: t, cat };
}

// Classification d'un doc contre la liste dynamique de types actifs.
// Retourne le `code` du type matché, sinon 'autre'.
// V36 (règle d3) : un doc SANS date est automatiquement routé vers 'autre',
// peu importe son type d'origine. La signature est un badge informatif et
// n'influe PAS sur le routage.
function classifyDoc(doc, types) {
  // Règle d3 — pas de date détectée → bucket "Autre"
  const hasDate = !!(doc?.effective_date || doc?.date_origine);
  if (!hasDate) return 'autre';
  const h = buildHaystack(doc);

  // 1. Match exact sur type_document / categorie === code (pour les ZIP uploads et docs récents)
  for (const t of types) {
    if (h.type === t.code || h.cat === t.code) return t.code;
  }

  // 2. Patterns par défaut (types historiques)
  for (const t of types) {
    const pat = DEFAULT_PATTERNS[t.code];
    if (pat && pat.test(h.full)) return t.code;
  }

  // 3. Matching custom : tokens du code (sans underscore) OU du label
  for (const t of types) {
    if (DEFAULT_PATTERNS[t.code]) continue; // déjà testé
    const tokens = [
      t.code.replace(/_/g, ' '),
      ...t.code.split('_').filter((s) => s.length >= 4),
      ...t.label.toLowerCase().split(/\s+/).filter((s) => s.length >= 4),
    ];
    if (tokens.some((tok) => h.full.includes(tok))) return t.code;
  }

  // 4. Fallback historique : devis_ess / attestation / conditions_generales → rcds_batico s'il existe
  if (['devis_ess', 'attestation', 'conditions_generales', 'recu_paiement'].includes(h.type)) {
    if (types.some((t) => t.code === 'rcds_batico')) return 'rcds_batico';
  }

  return 'autre';
}

// Rétrocompat (utilisé ailleurs potentiellement) — version legacy à 4 buckets.
export function classifySouscriptionType(doc) {
  const h = buildHaystack(doc);
  if (DEFAULT_PATTERNS.ftpe_adhesion.test(h.full)) return 'ftpe';
  if (DEFAULT_PATTERNS.rcds_batico.test(h.full)) return 'batico';
  if (DEFAULT_PATTERNS.dos_domium.test(h.full)) return 'domium';
  if (['devis_ess', 'attestation', 'conditions_generales', 'recu_paiement'].includes(h.type)) return 'batico';
  return 'autre';
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
}

// Bucket "Autre" virtuel (toujours présent en dernier).
const AUTRE_BUCKET = {
  id: '__autre__',
  code: 'autre',
  label: 'Autre',
  color: '#64748b',
  icon: 'folder',
  actif: true,
  ordre: 999,
};

export default function CategorizedDocsSection({ membre, token, selectable = false, selectedIds, onSelectionChange }) {
  const [allDocs, setAllDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openTabs, setOpenTabs] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'signed' | 'dated' | 'unindexed'

  // selectedIds est un Set ou Array fourni par le parent (controlled)
  const selectedSet = useMemo(() => {
    if (selectedIds instanceof Set) return selectedIds;
    return new Set(Array.isArray(selectedIds) ? selectedIds : []);
  }, [selectedIds]);

  const updateSelection = (next) => {
    if (typeof onSelectionChange === 'function') onSelectionChange(next);
  };

  const toggleDoc = (id) => {
    if (!id) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    updateSelection(next);
  };

  const toggleBucket = (docsInBucket) => {
    const ids = docsInBucket.map((d) => d.id).filter(Boolean);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);
    if (allSelected) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    updateSelection(next);
  };


  // Charge les types de souscription (lecture publique authentifiée)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/api/admin/subscription-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (cancelled) return;
        const active = (r.data.types || []).filter((t) => t.actif !== false);
        // Garantit le bucket "Autre" non-default
        const hasAutre = active.some((t) => t.code === 'autre');
        const finalTypes = hasAutre ? active : [...active, AUTRE_BUCKET];
        setTypes(finalTypes);
        // Onglets ouverts par défaut : tous les types non-vides après chargement docs (cf. effect docs)
        const initOpen = {};
        finalTypes.forEach((t) => { initOpen[t.code] = t.code !== 'autre'; });
        setOpenTabs(initOpen);
      })
      .catch(() => { if (!cancelled) setTypes([AUTRE_BUCKET]); });
    return () => { cancelled = true; };
  }, [token]);

  // Charge les documents du membre
  useEffect(() => {
    if (!membre?.id || !token) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/cooper/membres/${membre.id}/documents-et-simulations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setAllDocs(r.data.documents_contractuels || []); })
      .catch(() => { if (!cancelled) setAllDocs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [membre?.id, token]);

  // Filtre par statut d'indexation (signé / daté / non indexé)
  const filteredDocs = useMemo(() => {
    if (statusFilter === 'all') return allDocs;
    return allDocs.filter((d) => {
      const isSigned = !!d.is_signed;
      const isDated = !!(d.effective_date || d.date_origine);
      if (statusFilter === 'signed') return isSigned;
      if (statusFilter === 'dated') return isDated;
      if (statusFilter === 'unindexed') return !isSigned && !isDated;
      return true;
    });
  }, [allDocs, statusFilter]);

  // Groupement par code de type
  const byCode = useMemo(() => {
    const groups = {};
    types.forEach((t) => { groups[t.code] = []; });
    filteredDocs.forEach((d) => {
      const code = classifyDoc(d, types);
      if (!groups[code]) groups[code] = [];
      groups[code].push(d);
    });
    return groups;
  }, [filteredDocs, types]);

  const totalCount = allDocs.length;
  const totalSigned = useMemo(() => allDocs.filter((d) => d.is_signed).length, [allDocs]);
  const totalDated = useMemo(() => allDocs.filter((d) => d.effective_date || d.date_origine).length, [allDocs]);
  const totalUnindexed = useMemo(() => allDocs.filter((d) => !d.is_signed && !(d.effective_date || d.date_origine)).length, [allDocs]);
  const visibleTypes = activeFilter === 'all' ? types : types.filter((t) => t.code === activeFilter);

  return (
    <div className="space-y-3" data-testid="categorized-docs-section">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            Documents de souscription — catégorisés par type
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Tous les documents insérés du membre, ventilés dynamiquement selon les types de souscription configurés.
          </p>
        </div>
        <Badge variant="outline" className="text-xs font-semibold shrink-0">
          {loading ? 'chargement…' : `${totalCount} document${totalCount > 1 ? 's' : ''}`}
        </Badge>
      </div>

      {/* Filtre par type (pills) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="docs-type-filter">
        <button
          type="button"
          onClick={() => setActiveFilter('all')}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
            activeFilter === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-md'
              : 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200'
          }`}
          data-testid="filter-pill-all"
        >
          {activeFilter === 'all' && <Check className="h-3 w-3" />}
          Tous
          <span className="ml-0.5 opacity-80">({totalCount})</span>
        </button>
        {types.map((t) => {
          const Icon = ICON_MAP[t.icon] || Folder;
          const count = (byCode[t.code] || []).length;
          const isActive = activeFilter === t.code;
          return (
            <button
              key={t.code}
              type="button"
              onClick={() => setActiveFilter(isActive ? 'all' : t.code)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
                isActive ? 'text-white shadow-md' : 'hover:bg-slate-50'
              }`}
              style={
                isActive
                  ? { backgroundColor: t.color, borderColor: t.color }
                  : { borderColor: t.color, color: t.color, backgroundColor: `${t.color}1a` }
              }
              data-testid={`filter-pill-${t.code}`}
            >
              {isActive && <Check className="h-3 w-3" />}
              <Icon className="h-3 w-3" style={isActive ? undefined : { color: t.color }} />
              {t.label}
              <span className="ml-0.5 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Filtre par statut d'indexation (signed / dated / unindexed) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="docs-status-filter">
        <span className="text-[10px] uppercase font-semibold text-slate-400 mr-1">Statut :</span>
        {[
          { code: 'all', label: 'Tous', count: totalCount, icon: null, color: '#475569' },
          { code: 'signed', label: 'Signés', count: totalSigned, icon: PenLine, color: '#059669' },
          { code: 'dated', label: 'Datés', count: totalDated, icon: CalendarCheck, color: '#0284c7' },
          { code: 'unindexed', label: 'Non indexés', count: totalUnindexed, icon: null, color: '#94a3b8' },
        ].map((f) => {
          const isActive = statusFilter === f.code;
          const Icon = f.icon;
          return (
            <button
              key={f.code}
              type="button"
              onClick={() => setStatusFilter(f.code === 'all' ? 'all' : isActive ? 'all' : f.code)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
                isActive ? 'text-white shadow-md' : 'hover:bg-slate-50'
              }`}
              style={
                isActive
                  ? { backgroundColor: f.color, borderColor: f.color }
                  : { borderColor: f.color, color: f.color, backgroundColor: `${f.color}1a` }
              }
              data-testid={`status-pill-${f.code}`}
            >
              {isActive && <Check className="h-3 w-3" />}
              {Icon && <Icon className="h-3 w-3" style={isActive ? undefined : { color: f.color }} />}
              {f.label}
              <span className="ml-0.5 opacity-80">({f.count})</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {visibleTypes.map((t) => {
          const docs = byCode[t.code] || [];
          const Icon = ICON_MAP[t.icon] || Folder;
          const isOpen = openTabs[t.code] ?? true;
          const tint = t.color || '#64748b';
          // État sélection bucket
          const bucketIds = docs.map((d) => d.id).filter(Boolean);
          const bucketSelectedCount = bucketIds.filter((id) => selectedSet.has(id)).length;
          const bucketAllSelected = bucketIds.length > 0 && bucketSelectedCount === bucketIds.length;
          const bucketSomeSelected = bucketSelectedCount > 0 && bucketSelectedCount < bucketIds.length;
          return (
            <div
              key={t.code}
              className="border-2 rounded-lg overflow-hidden shadow-sm"
              style={{ borderColor: tint, backgroundColor: `${tint}1f` }}
              data-testid={`docs-category-${t.code}`}
            >
              <div className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/40 transition-colors">
                <button
                  type="button"
                  onClick={() => setOpenTabs((p) => ({ ...p, [t.code]: !p[t.code] }))}
                  className="flex items-center gap-2 min-w-0 flex-1 text-left"
                >
                  <span
                    className="w-7 h-7 rounded text-white flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: tint }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="font-bold text-sm truncate" style={{ color: tint }}>{t.label}</span>
                  <span
                    className="inline-flex items-center justify-center min-w-[24px] h-[20px] px-1.5 rounded-full bg-white border-2 text-[11px] font-extrabold shadow-sm"
                    style={{ color: tint, borderColor: tint }}
                    data-testid={`docs-count-badge-${t.code}`}
                  >
                    {selectable && bucketSelectedCount > 0
                      ? `${bucketSelectedCount}/${docs.length}`
                      : docs.length}
                  </span>
                </button>
                {selectable && docs.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleBucket(docs); }}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded mr-2 border transition-colors ${
                      bucketAllSelected
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    title={bucketAllSelected ? 'Tout décocher dans ce bucket' : 'Tout cocher dans ce bucket'}
                    data-testid={`bucket-toggle-${t.code}`}
                  >
                    {bucketAllSelected ? '✓ Tout' : bucketSomeSelected ? `${bucketSelectedCount}/${docs.length}` : 'Tout cocher'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpenTabs((p) => ({ ...p, [t.code]: !p[t.code] }))}
                  className="ml-1"
                >
                  {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                </button>
              </div>
              {isOpen && (
                <div className="bg-white/95 border-t border-current/20 max-h-[260px] overflow-y-auto">
                  {docs.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-slate-400">
                      Aucun document
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {docs.map((doc, i) => {
                        const dateOrig = doc.date_origine || doc.effective_date;
                        const isAntecedent = !!(doc.is_antecedent || (dateOrig && new Date(dateOrig) < new Date('2026-05-01')));
                        const isChecked = selectable && doc.id && selectedSet.has(doc.id);
                        return (
                          <li
                            key={doc.id || i}
                            className={`px-3 py-2 flex items-start gap-2 hover:bg-slate-50 ${isChecked ? 'bg-indigo-50/50' : ''}`}
                          >
                            {selectable && (
                              <button
                                type="button"
                                onClick={() => toggleDoc(doc.id)}
                                disabled={!doc.id}
                                className={`mt-0.5 h-3.5 w-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                                  isChecked ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300 hover:border-indigo-400'
                                } ${!doc.id ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                data-testid={`doc-check-${doc.id || i}`}
                                title={doc.id ? 'Inclure dans l\'export' : 'ID indisponible'}
                              >
                                {isChecked && <Check className="h-2.5 w-2.5 text-white" />}
                              </button>
                            )}
                            <FileText className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-slate-800 truncate" title={doc.nom || doc.titre}>
                                {doc.nom || doc.titre || doc.label || doc.reference || `Document #${doc.id?.slice(0, 6)}`}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5 flex-wrap">
                                {doc.type && <span className="font-mono">{doc.type}</span>}
                                {dateOrig && (
                                  <span className="inline-flex items-center gap-0.5">
                                    <Clock className="h-2.5 w-2.5" /> {formatDate(dateOrig)}
                                  </span>
                                )}
                                {/* V36 — Badges indexation auto */}
                                {doc.is_signed && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-300 px-1 py-0.5 rounded uppercase"
                                    title={`Signature détectée${doc.signature_kind && doc.signature_kind !== 'none' ? ` (${doc.signature_kind})` : ''}`}
                                  >
                                    <PenLine className="h-2.5 w-2.5" /> Signé
                                  </span>
                                )}
                                {doc.effective_date && doc.date_source && doc.date_source !== 'manual_admin' && (
                                  <span
                                    className="inline-flex items-center gap-0.5 text-[9px] font-bold text-sky-800 bg-sky-100 border border-sky-300 px-1 py-0.5 rounded uppercase"
                                    title={`Date auto-détectée (${doc.date_source}, confiance ${doc.confidence_date || '?'})`}
                                  >
                                    <CalendarCheck className="h-2.5 w-2.5" /> Daté auto
                                  </span>
                                )}
                                {doc.indexed === false && (
                                  <span
                                    className="text-[9px] font-bold text-slate-500 bg-slate-100 border border-slate-300 px-1 py-0.5 rounded uppercase"
                                    title="Document non encore analysé"
                                  >
                                    Non indexé
                                  </span>
                                )}
                                {isAntecedent && (
                                  <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1 py-0.5 rounded uppercase">
                                    Antécédent
                                  </span>
                                )}
                                {(doc.source === 'admin_upload' || doc.source === 'admin_upload_zip') && (
                                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded uppercase">
                                    GED ✓
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
